<template>
  <AdminLayout>
    <div class="d-flex align-items-center justify-content-between mb-4">
      <h2 class="text-white mb-0" style="font-weight:800">Blog Posts</h2>
      <RouterLink to="/admin/blog/new" class="btn">
        <span class="border-style"><i class="ti ti-plus"></i> New Post</span>
      </RouterLink>
    </div>

    <div v-if="loading" class="text-silver">Loading posts...</div>

    <div v-else-if="posts.length === 0" class="text-silver">No posts yet. Create your first one.</div>

    <div v-else class="blog-admin-table-wrap">
      <table class="blog-admin-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Status</th>
            <th>Published</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="post in posts" :key="post.id">
            <td>
              <span class="post-title">{{ post.title }}</span>
              <span class="post-slug">/blog/{{ post.slug }}</span>
            </td>
            <td>
              <span :class="['status-badge', post.status]">{{ post.status }}</span>
            </td>
            <td class="text-silver">{{ post.published_at ? formatDate(post.published_at) : '—' }}</td>
            <td>
              <div class="d-flex gap-2">
                <RouterLink :to="'/admin/blog/' + post.id" class="table-action-btn edit">
                  <i class="ti ti-edit"></i> Edit
                </RouterLink>
                <a
                  v-if="post.status === 'published'"
                  :href="'/blog/' + post.slug"
                  target="_blank"
                  class="table-action-btn view"
                >
                  <i class="ti ti-external-link"></i> View
                </a>
                <button class="table-action-btn delete" @click="deletePost(post)">
                  <i class="ti ti-trash"></i>{{ pendingDeleteId === post.id ? ' Confirm?' : '' }}
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="totalPages > 1" class="d-flex align-items-center gap-3 mt-4">
      <button class="btn" :disabled="page === 1" @click="loadPage(page - 1)">
        <span class="border-style">Prev</span>
      </button>
      <span class="text-silver">{{ page }} / {{ totalPages }}</span>
      <button class="btn" :disabled="page === totalPages" @click="loadPage(page + 1)">
        <span class="border-style">Next</span>
      </button>
    </div>
  </AdminLayout>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import AdminLayout from "@/components/admin/AdminLayout.vue";
import api from "@/services/api";

export default defineComponent({
  name: "BlogListPage",
  components: { AdminLayout },
  setup() {
    const posts = ref<any[]>([]);
    const loading = ref(true);
    const page = ref(1);
    const totalPages = ref(1);

    async function loadPage(p: number) {
      loading.value = true;
      try {
        const { data } = await api.get(`/api/admin/blog?page=${p}`);
        posts.value = data.data;
        page.value = data.current_page;
        totalPages.value = data.last_page;
      } finally {
        loading.value = false;
      }
    }

    // Two-step delete — window.confirm() silently fails in embedded browsers
    const pendingDeleteId = ref<number | null>(null);
    let pendingDeleteTimer: ReturnType<typeof setTimeout> | null = null;

    async function deletePost(post: any) {
      if (pendingDeleteId.value !== post.id) {
        pendingDeleteId.value = post.id;
        if (pendingDeleteTimer) clearTimeout(pendingDeleteTimer);
        pendingDeleteTimer = setTimeout(() => (pendingDeleteId.value = null), 3000);
        return;
      }
      pendingDeleteId.value = null;
      await api.delete(`/api/admin/blog/${post.id}`);
      posts.value = posts.value.filter((p) => p.id !== post.id);
    }

    const formatDate = (iso: string) =>
      new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

    onMounted(() => loadPage(1));

    return { posts, loading, page, totalPages, loadPage, deletePost, pendingDeleteId, formatDate };
  },
});
</script>

<style scoped>
.blog-admin-table-wrap { overflow-x: auto; }
.blog-admin-table {
  width: 100%; border-collapse: collapse;
  background: #140d0e; border-radius: 12px; overflow: hidden;
}
.blog-admin-table th {
  background: #1e1112; color: #c0c0c0;
  font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.05em;
  padding: 0.75rem 1rem; text-align: left;
}
.blog-admin-table td {
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #2a1a1b; vertical-align: middle;
}
.blog-admin-table tr:last-child td { border-bottom: none; }

.post-title { display: block; color: #fff; font-weight: 600; font-size: 0.95rem; }
.post-slug { display: block; color: #666; font-size: 0.75rem; margin-top: 2px; }

.status-badge {
  display: inline-block; padding: 0.2rem 0.6rem;
  border-radius: 20px; font-size: 0.75rem; font-weight: 600; text-transform: capitalize;
}
.status-badge.published { background: rgba(74,222,128,0.15); color: #4ade80; }
.status-badge.draft { background: rgba(251,191,36,0.15); color: #fbbf24; }

.table-action-btn {
  display: inline-flex; align-items: center; gap: 0.25rem;
  padding: 0.3rem 0.65rem; border-radius: 6px;
  font-size: 0.8rem; font-weight: 600; cursor: pointer;
  border: none; text-decoration: none; transition: all 0.2s;
}
.table-action-btn.edit { background: rgba(96,165,250,0.15); color: #60a5fa; }
.table-action-btn.edit:hover { background: rgba(96,165,250,0.3); }
.table-action-btn.view { background: rgba(74,222,128,0.1); color: #4ade80; }
.table-action-btn.view:hover { background: rgba(74,222,128,0.25); }
.table-action-btn.delete { background: rgba(201,14,14,0.15); color: #ff6b6b; }
.table-action-btn.delete:hover { background: rgba(201,14,14,0.3); }
</style>
